import { Fragment, useCallback, useContext, useMemo } from 'react';
import { LinePath, Area, getFirstItem, getSecondItem } from '@visx/shape';
import { coerceNumber } from '@visx/scale';
import DataContext from "../../../context/DataContext.js";
import { BaseGlyphSeries } from "./BaseGlyphSeries.js";
import useStackedData from "../../../hooks/useStackedData.js";
import { getStackValue } from "../../../utils/combineBarStackData.js";
import isValidNumber from "../../../typeguards/isValidNumber.js";
import findNearestStackDatum from "../../../utils/findNearestStackDatum.js";
import { AREASTACK_EVENT_SOURCE, XYCHART_EVENT_SOURCE } from "../../../constants.js";
import useSeriesEvents from "../../../hooks/useSeriesEvents.js";
import defaultRenderGlyph from "./defaultRenderGlyph.js";
import getScaleBandwidth from "../../../utils/getScaleBandwidth.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const identity = _ => _;
function BaseAreaStack(_ref) {
  let {
    PathComponent = 'path',
    children,
    curve,
    enableEvents = true,
    offset,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    order,
    renderLine = true
  } = _ref;
  const {
    colorScale,
    dataRegistry,
    horizontal,
    xScale,
    yScale,
    theme
  } = useContext(DataContext);
  const {
    dataKeys,
    seriesChildren,
    stackedData
  } = useStackedData({
    children,
    order,
    offset
  });

  // accessor functions for the stack generator
  const accessors = useMemo(() => {
    const xOffset = getScaleBandwidth(xScale) / 2;
    const yOffset = getScaleBandwidth(yScale) / 2;
    return horizontal ? {
      y: d => (coerceNumber(yScale(getStackValue(d.data))) ?? 0) + yOffset,
      x0: d => (coerceNumber(xScale(getFirstItem(d))) ?? 0) + xOffset,
      x1: d => (coerceNumber(xScale(getSecondItem(d))) ?? 0) + xOffset,
      defined: d => isValidNumber(yScale(getStackValue(d.data))) && isValidNumber(xScale(getSecondItem(d)))
    } : {
      x: d => (coerceNumber(xScale(getStackValue(d.data))) ?? 0) + xOffset,
      y0: d => (coerceNumber(yScale(getFirstItem(d))) ?? 0) + yOffset,
      y1: d => (coerceNumber(yScale(getSecondItem(d))) ?? 0) + yOffset,
      defined: d => isValidNumber(xScale(getStackValue(d.data))) && isValidNumber(yScale(getSecondItem(d)))
    };
  }, [xScale, yScale, horizontal]);

  // pull out all area + line props for each dataKey
  const stacks = useMemo(() => stackedData.map((stack, stackIndex) => {
    const areaSeries = seriesChildren.find(child => child.props.dataKey === stack.key);
    const {
      data,
      dataKey,
      xAccessor,
      yAccessor,
      curve: _,
      PathComponent: __,
      lineProps,
      renderLine: ___,
      ...svgPathProps
    } = areaSeries?.props || {};
    const areaProps = {
      fill: colorScale?.(stack.key) ?? theme?.colors?.[0] ?? '#222',
      ...svgPathProps
    };
    return {
      key: `${stackIndex}-${stack.key}`,
      accessors,
      data: stack,
      areaProps,
      lineProps
    };
  }), [stackedData, accessors, colorScale, seriesChildren, theme]);

  // custom logic to find the nearest AreaStackDatum (context) and return the original Datum (props)
  const findNearestDatum = useCallback(params => {
    const childData = seriesChildren.find(child => child.props.dataKey === params.dataKey)?.props?.data;
    return childData ? findNearestStackDatum(params, childData, horizontal) : null;
  }, [seriesChildren, horizontal]);
  const ownEventSourceKey = `${AREASTACK_EVENT_SOURCE}-${dataKeys.join('-')}`;
  const eventEmitters = useSeriesEvents({
    dataKey: dataKeys,
    enableEvents,
    // @ts-expect-error Datum input + return type are expected to be the same type but they differ
    // for AreaStack (registry data is StackedDatum, return type is user Datum)
    findNearestDatum,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    source: ownEventSourceKey,
    allowedSources: [XYCHART_EVENT_SOURCE, ownEventSourceKey]
  });

  // render invisible glyphs for focusing if onFocus/onBlur are defined
  const captureFocusEvents = Boolean(onFocus || onBlur);
  const renderGlyphs = useCallback(_ref2 => {
    let {
      glyphs
    } = _ref2;
    return captureFocusEvents ? glyphs.map(glyph => /*#__PURE__*/_jsx(Fragment, {
      children: defaultRenderGlyph({
        ...glyph,
        color: 'transparent',
        onFocus: eventEmitters.onFocus,
        onBlur: eventEmitters.onBlur
      })
    }, glyph.key)) : null;
  }, [captureFocusEvents, eventEmitters.onFocus, eventEmitters.onBlur]);

  // if scales and data are not available in the registry, bail
  if (dataKeys.some(key => dataRegistry.get(key) == null) || !xScale || !yScale || !colorScale) {
    return null;
  }
  return /*#__PURE__*/_jsxs("g", {
    className: "visx-area-stack",
    children: [stacks.map(stack => /*#__PURE__*/_jsx(Area, {
      curve: curve,
      ...stack.accessors,
      children: _ref3 => {
        let {
          path
        } = _ref3;
        return /*#__PURE__*/_jsx(PathComponent, {
          className: "visx-area",
          stroke: "transparent",
          d: path(stack.data) || '',
          ...stack.areaProps,
          ...eventEmitters
        });
      }
    }, stack.key)), renderLine && stacks.map(stack => /*#__PURE__*/_jsx(LinePath, {
      // note: this currently doesn't work well for offset=wiggle
      // because it only draws a single line. with two lines you
      // get overlap across stacks :/
      x: stack.accessors.x || stack.accessors.x1,
      y: stack.accessors.y || stack.accessors.y1,
      defined: stack.accessors.defined,
      curve: curve,
      ...stack.lineProps,
      children: _ref4 => {
        let {
          path
        } = _ref4;
        return /*#__PURE__*/_jsx(PathComponent, {
          className: "visx-line",
          fill: "transparent",
          stroke: stack.areaProps.fill,
          strokeWidth: 2,
          pointerEvents: "none",
          ...stack.lineProps,
          d: path(stack.data) || ''
        });
      }
    }, `line-${stack.key}`)), captureFocusEvents && stacks.map((_, i) => {
      // render in reverse stack order tab to top-values first
      const stack = stacks[stacks.length - i - 1];
      return /*#__PURE__*/_jsx(BaseGlyphSeries, {
        dataKey: stack.key,
        data: stack.data,
        xAccessor: stack.accessors.x || stack.accessors.x1,
        yAccessor: stack.accessors.y || stack.accessors.y1
        // accessors include scaling, so just return the scaled value
        ,
        xScale: identity,
        yScale: identity,
        renderGlyphs: renderGlyphs
      }, `glyphs-${stack.key}`);
    })]
  });
}
export default BaseAreaStack;